"use client";

import { useEffect, useState } from "react";
import type { ThemePreference } from "./use-theme-mode";

/**
 * Singleton runtime bus for visitor-preference changes in DEMO mode.
 *
 * Live mode persists preferences through the BE (`updateVisitorProfile`
 * mutation → Apollo cache → `useVisitorProfilePreferences` re-read).
 * Demo mode has no BE: the Profile tab's "save" is a local-only state
 * flip, but we still want changes to flow EVERYWHERE the widget reads
 * preferences — `useThemeMode` (chrome theme), `useVisitorProfilePreferences`
 * (language → `RuntimeIntlProvider`), notification opt-in, etc.
 *
 * Without a global bus the changes only live inside `ProfileTab`'s
 * local state and reset the moment the visitor switches to another
 * tab (since `ProfileTab` unmounts on tab-switch in some layouts).
 *
 * Module-level singleton — there's only ever one widget instance per
 * page, so a single shared `state` is fine. React consumers subscribe
 * via `useDemoPrefs()`; ad-hoc consumers can call `getDemoPrefs()`.
 */
export interface DemoVisitorPrefs {
  themeMode: ThemePreference | null;
  language: string | null;
  desktopNotifications: boolean;
}

const INITIAL: DemoVisitorPrefs = {
  themeMode: null,
  language: null,
  desktopNotifications: false,
};

let state: DemoVisitorPrefs = INITIAL;
const listeners = new Set<(prefs: DemoVisitorPrefs) => void>();

export function setDemoPref<K extends keyof DemoVisitorPrefs>(
  key: K,
  value: DemoVisitorPrefs[K],
): void {
  if (state[key] === value) return;
  state = { ...state, [key]: value };
  for (const fn of listeners) fn(state);
}

export function getDemoPrefs(): DemoVisitorPrefs {
  return state;
}

/** Subscribe to demo-pref changes — returns the current snapshot and
 *  re-renders the consumer whenever any pref changes via `setDemoPref`. */
export function useDemoPrefs(): DemoVisitorPrefs {
  const [snapshot, setSnapshot] = useState(state);
  useEffect(() => {
    listeners.add(setSnapshot);
    // Pick up any value that was set BEFORE this component mounted.
    setSnapshot(state);
    return () => {
      listeners.delete(setSnapshot);
    };
  }, []);
  return snapshot;
}
